/**
 * Single source of truth for SEO/GEO site-level constants for the microsite.
 *
 * SITE_URL is baked at build time from VITE_SITE_URL (set in .env.production).
 * This matters for build-time prerendering: puppeteer renders the built bundle
 * from a localhost preview server, but VITE_SITE_URL is already inlined, so
 * canonical/og:url/sitemap URLs resolve to the real production origin
 * (https://www.bullchargeratrace.com) rather than localhost. It defaults to the
 * production origin so even a plain `vite build` bakes correct absolute URLs.
 */

const RAW_SITE_URL =
  (import.meta.env.VITE_SITE_URL as string | undefined) || "https://www.bullchargeratrace.com";

/** Production origin with any trailing slash trimmed, e.g. "https://www.bullchargeratrace.com". */
export const SITE_URL = RAW_SITE_URL.replace(/\/+$/, "");

/** Build an absolute URL for a site-relative path. `absoluteUrl("/race-day") → https://www.bullchargeratrace.com/race-day`. */
export function absoluteUrl(path = "/"): string {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${suffix}`;
}

// --- Brand / event facts (reused by JSON-LD builders + <title>s) ---
export const SITE_NAME = "Bursa Bull Charge × The Edge KL Rat Race 2026";
export const ORG_NAME = "Bursa Bull Charge × The Edge KL Rat Race";
export const ORG_CONTACT_EMAIL = "BBCKLRR@bizedge.com";
export const ORG_LOGO_URL = absoluteUrl("/BBCxTEKLRR2026_Logo-PNG.png");

/** Default social/AI share image — the 2026 landscape key visual. */
export const DEFAULT_OG_IMAGE = absoluteUrl("/key-visual-v2-landscape.jpeg");

export const SITE_DESCRIPTION =
  "The Bursa Bull Charge × The Edge KL Rat Race 2026 is a corporate relay race by Bursa Malaysia and The Edge through Kuala Lumpur's financial district on 28 November 2026. Teams of four run for financial literacy and disability inclusion — Run as One. Run for All.";

/**
 * Append the event brand to a page label for a full document <title>.
 * `pageTitle("Race Day") → "Race Day | Bursa Bull Charge × The Edge KL Rat Race 2026"`.
 */
export function pageTitle(label: string): string {
  return `${label} | ${SITE_NAME}`;
}

// --- Event schedule + venue (schema.org/SportsEvent). Local time is Asia/Kuala_Lumpur (+08:00). ---
export const EVENT_START_ISO = "2026-11-28T06:30:00+08:00"; // 06:30 AM, Sat 28 Nov 2026 KL
export const EVENT_END_ISO = "2026-11-28T13:00:00+08:00"; // 13:00, Sat 28 Nov 2026 KL
export const EVENT_VENUE_NAME = "Bursa Malaysia, Exchange Square";
export const EVENT_STREET_ADDRESS = "Exchange Square, Bukit Kewangan";
export const EVENT_LOCALITY = "Kuala Lumpur";
export const EVENT_POSTAL_CODE = "50200";
export const EVENT_COUNTRY = "MY";
